import React from "react";
import {
  Box,
  Autocomplete,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ClassFiltersProps } from "../interfaces/classFilters";
import {
  DAY_OPTIONS,
  CLASS_TYPE_OPTIONS,
} from "../utils/const";
import themestyle from "../theme";

const ClassFilters: React.FC<ClassFiltersProps> = ({
  instructorValue,
  dayValue,
  classTypeValue,
  onInstructorChange,
  onDayChange,
  onClassTypeChange,
  instructorOptions = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: isMobile ? 0.3 : 2,
        boxSizing: "border-box",
        width: isMobile ? "95%" : "100%", 
        mx: isMobile ? "auto" : 0,      
      }}
    >
      <Autocomplete
        size={isMobile ? "small" : "medium"}
        options={instructorOptions}
        getOptionLabel={(option: any) => (typeof option === 'string' ? option : option.fullname || "")}
        value={instructorValue}
        onChange={(event, newValue: any) => {
          onInstructorChange(typeof newValue === 'string' ? newValue : newValue?.fullname || null);
        }}
        disableClearable={false}
        forcePopupIcon={isMobile ? !instructorValue : true}
        sx={{
          flex: 1,
          minWidth: 0,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            placeholder={isMobile ? "Instructor" : "Filter by Instructor"}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                backgroundColor: themestyle.colors.white,
                fontSize: isMobile ? 12 : 14,
                minHeight: isMobile ? 45 : 52,
                px: isMobile ? 1 : 2,
                "& fieldset": {
                  borderWidth: "1px",
                  borderColor: themestyle.colors.bordercolor,
                },
                "&:hover fieldset": {
                  borderColor: themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
                "&.Mui-focused fieldset": {
                  borderColor: themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": {
                height: isMobile ? "25px" : "auto",
              }
            }}
          />
        )}
        slotProps={{
          listbox: {
            sx: {
              fontSize: isMobile ? 11 : 14,
              "& .MuiAutocomplete-option": {
                padding: isMobile ? "4px 8px" : "6px 16px",
                minHeight: isMobile ? "auto" : undefined,
              },
            },
          },
          popper: isMobile ? {
            placement: "bottom-end",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 4],
                },
              },
            ],
          } : undefined,
        }}
      />
      <Autocomplete
        size={isMobile ? "small" : "medium"}
        options={DAY_OPTIONS}
        value={dayValue}
        onChange={(event, newValue) => {
          onDayChange(newValue);
        }}
        disableClearable={false}
        forcePopupIcon={isMobile ? !dayValue : true}
        sx={{
          flex: 1,
          minWidth: 0,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            placeholder={isMobile ? "Day" : "Filter by Day"}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                backgroundColor: themestyle.colors.white,
                fontSize: isMobile ? 12 : 14,
                minHeight: isMobile ? 45 : 52,
                px: isMobile ? 1 : 2,
                "& fieldset": {
                  borderWidth: "1px",
                  borderColor: themestyle.colors.bordercolor,
                },
                "&:hover fieldset": {
                  borderColor:themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
                "&.Mui-focused fieldset": {
                  borderColor:themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": {
                height: isMobile ? "25px" : "auto",
              }
            }}
          />
        )}
        slotProps={{
          listbox: {
            sx: {
              fontSize: isMobile ? 11 : 14,
              "& .MuiAutocomplete-option": {
                padding: isMobile ? "4px 8px" : "6px 16px",
                minHeight: isMobile ? "auto" : undefined,
              },
            },
          },
          popper: isMobile ? {
            placement: "bottom-end",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 4],
                },
              },
            ],
          } : undefined,
        }}
      />
      <Autocomplete
        size={isMobile ? "small" : "medium"}
        options={CLASS_TYPE_OPTIONS}
        getOptionLabel={(option) => option.label}
        value={CLASS_TYPE_OPTIONS.find((type) => type.value === classTypeValue) || null}
        onChange={(event, newValue) => {
          onClassTypeChange(newValue ? newValue.value : null);
        }}
        disableClearable={false}
        forcePopupIcon={isMobile ? !classTypeValue : true}
        sx={{
          flex: 1,
          minWidth: 0,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            placeholder={isMobile ? "Type" : "Filter by Type"}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                backgroundColor: themestyle.colors.white,
                fontSize: isMobile ? 12 : 14,
                minHeight: isMobile ? 45 : 52,
                px: isMobile ? 1 : 2,
                "& fieldset": {
                  borderWidth: "1px",
                  borderColor: "#E5E7EB",
                },
                "&:hover fieldset": {
                  borderColor: themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
                "&.Mui-focused fieldset": {
                  borderColor: themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": {
                height: isMobile ? "25px" : "auto",
              }
            }}
          />
        )}
        slotProps={{
          listbox: {
            sx: {
              fontSize: isMobile ? 11 : 14,
              "& .MuiAutocomplete-option": {
                padding: isMobile ? "4px 8px" : "6px 16px",
                minHeight: isMobile ? "auto" : undefined,
              },
            },
          },
          popper: isMobile ? {
            placement: "bottom-end",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 4],
                },
              },
            ],
          } : undefined,
        }}
      />
    </Box>
  );
};
export default ClassFilters;
